<template>
    <div
        ref="rootRef"
        class="ds-dept-tree"
        :class="`ds-dept-tree--${mode}`"
        v-click-outside="onClickOutside"
        @focusout="onFocusOut"
        @keydown.esc="onEscape"
    >
        <!-- Единственный доступный магазин: подпись без фона, стрелки и меню. -->
        <div v-if="isSingleStore" class="ds-dept-tree-trigger ds-dept-tree-trigger--static">
            <icon width="16" height="16" class="ds-dept-tree-trigger-icon"><IconMapPin /></icon>
            <span class="ds-dept-tree-trigger-text">{{ triggerLabel }}</span>
        </div>
        <button
            v-else
            ref="triggerRef"
            type="button"
            class="ds-dept-tree-trigger"
            aria-haspopup="tree"
            :aria-expanded="isOpen"
            :title="triggerLabel"
            @click="toggleMenu"
            @keydown.down.prevent="openMenu"
        >
            <icon v-if="selectedGroup" width="24" height="24" :size="2 / 3" class="ds-dept-tree-trigger-icon"><IconList /></icon>
            <icon v-else width="16" height="16" class="ds-dept-tree-trigger-icon"><IconMapPin /></icon>
            <span class="ds-dept-tree-trigger-text">{{ triggerLabel }}</span>
            <span v-if="selectedGroupCount !== null" class="ds-dept-tree-count">{{ selectedGroupCount }} маг.</span>
            <icon width="9" height="13" class="ds-dept-tree-trigger-chevron" :class="{ 'is-open': isOpen }"><IconChevronRight /></icon>
        </button>

        <div
            v-if="isOpen"
            class="ds-dept-tree-menu"
            role="dialog"
            aria-label="Подразделение"
        >
            <div class="ds-dept-tree-search">
                <label class="ds-dept-tree-search-field">
                    <icon width="16" height="16" class="ds-dept-tree-search-icon"><IconSearch /></icon>
                    <input
                        ref="searchRef"
                        v-model="query"
                        type="search"
                        class="ds-dept-tree-search-input"
                        placeholder="Магазин, адрес или группа"
                        aria-label="Найти подразделение"
                        autocomplete="off"
                        @keydown.down.prevent="focusActiveRow"
                    >
                </label>
            </div>
            <div class="ds-dept-tree-divider" />

            <div class="ds-dept-tree-body">
                <div v-if="showLoader" class="ds-dept-tree-state">
                    <Loader size="32" />
                </div>
                <!-- Ошибка показывается и при ранее загруженном дереве: оно остаётся ниже, выбор не меняется. -->
                <div
                    v-if="loadError"
                    class="ds-dept-tree-state"
                    :class="{ 'is-compact': hasTreeData }"
                    role="alert"
                >
                    <span>Не удалось загрузить подразделения</span>
                    <button type="button" class="ds-glass-btn ds-glass-btn-primary" @click="loadTree">Повторить</button>
                </div>
                <div v-if="isEmpty" class="ds-dept-tree-state">Нет доступных подразделений</div>
                <div v-else-if="hasTreeData && !rows.length" class="ds-dept-tree-state">Ничего не найдено</div>
                <div v-else-if="rows.length" ref="treeRef" role="tree" aria-label="Подразделения" @keydown="onTreeKeydown">
                    <div
                        v-for="row in rows"
                        :key="row.key"
                        :data-key="row.key"
                        role="treeitem"
                        class="ds-dept-tree-row"
                        :class="{
                            'is-selected': row.key === selectedEntry?.key,
                            'is-ancestor': selectedAncestors.has(row.key),
                            'is-group': row.isGroup,
                            'is-disabled': !row.isGroup && !row.isStore,
                        }"
                        :style="{ paddingLeft: `${6 + 20 * indentLevel(row)}px` }"
                        :aria-level="row.depth + 1"
                        :aria-expanded="row.isGroup ? isExpanded(row) : undefined"
                        :aria-selected="row.key === selectedEntry?.key"
                        :aria-disabled="!row.isGroup && !row.isStore ? true : undefined"
                        :tabindex="row.key === activeKey ? 0 : -1"
                        @focus="activeKey = row.key"
                        @click="activateRow(row)"
                    >
                        <span
                            v-for="level in indentLevel(row)"
                            :key="level"
                            class="ds-dept-tree-guide"
                            :style="{ left: `${4 + 20 * level}px` }"
                            aria-hidden="true"
                        />
                        <div class="ds-dept-tree-plate">
                            <button
                                v-if="row.isGroup"
                                type="button"
                                tabindex="-1"
                                class="ds-dept-tree-chevron"
                                :class="{ 'is-expanded': isExpanded(row) }"
                                :aria-label="isExpanded(row) ? 'Свернуть' : 'Раскрыть'"
                                @click.stop="toggleRow(row)"
                            >
                                <icon width="9" height="13"><IconChevronRight /></icon>
                            </button>
                            <span v-else class="ds-dept-tree-chevron-spacer" aria-hidden="true" />

                            <span class="ds-dept-tree-name">
                                <template v-for="(part, index) in highlight(row.node.name)" :key="index">
                                    <mark v-if="part.match" class="ds-dept-tree-mark">{{ part.text }}</mark>
                                    <template v-else>{{ part.text }}</template>
                                </template>
                            </span>
                            <span v-if="!row.isGroup && row.address && row.address !== row.node.name" class="ds-dept-tree-address">
                                <template v-for="(part, index) in highlight(row.address)" :key="index">
                                    <mark v-if="part.match" class="ds-dept-tree-mark">{{ part.text }}</mark>
                                    <template v-else>{{ part.text }}</template>
                                </template>
                            </span>

                            <svg
                                v-if="row.key === selectedEntry?.key"
                                class="ds-dept-tree-check"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2.2"
                                aria-hidden="true"
                            ><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                            <template v-else-if="row.isGroup">
                                <span v-if="row.storeCount !== null" class="ds-dept-tree-count">{{ row.storeCount }} маг.</span>
                                <button
                                    v-if="canSelectGroup"
                                    type="button"
                                    class="ds-dept-tree-select-group"
                                    :tabindex="row.key === activeKey ? 0 : -1"
                                    @click.stop="selectGroup(row)"
                                    @keydown.enter.stop
                                    @keydown.space.stop
                                >
                                    Выбрать группу<template v-if="row.storeCount"> · {{ row.storeCount }}</template>
                                </button>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import IconMapPin from '@/ui/icons/IconMapPin.vue'
import IconChevronRight from '@/ui/icons/IconChevronRight.vue'
import IconSearch from '@/ui/icons/IconSearch.vue'
import IconList from '@/ui/icons/IconList.vue'
import Loader from '@/ui/Loader.vue'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useDepartmentStore } from '@/stores/departmentStore'
import {
    MAX_INDENT_LEVEL,
    ancestorKeys,
    buildDepartmentTree,
    findEntryById,
    normalizeQuery,
    normalizeTreeRoots,
    searchTree,
    splitByQuery,
    visibleEntries,
    type DepartmentTreeEntry,
} from './departmentTree'

// analytics — можно выбрать голову целиком; schedule — только магазин.
const props = withDefaults(defineProps<{ mode?: 'analytics' | 'schedule' }>(), { mode: 'schedule' })

const departmentStore = useDepartmentStore()
const scheduleStore = useScheduleStore()

const rootRef = ref<HTMLElement | null>(null)
const triggerRef = ref<HTMLButtonElement | null>(null)
const searchRef = ref<HTMLInputElement | null>(null)
const treeRef = ref<HTMLElement | null>(null)

const isOpen = ref(false)
const query = ref('')
const expanded = ref(new Set<string>())
const searchExpanded = ref(new Set<string>())
const activeKey = ref<string | null>(null)
const loading = ref(!departmentStore.departmentsTree)
const loadError = ref(false)
let selectionResolved = false

const canSelectGroup = computed(() => props.mode === 'analytics')

// Доступные магазины — уже загруженный список /api/departments/me/?only_leafs=true; пустой список = неизвестно.
const storeIds = computed(() => {
    const stores = departmentStore.myDepartments ?? []
    return stores.length ? new Set(stores.map((item) => item.id)) : null
})

const addresses = computed(() => {
    const map = new Map<string, string>()
    for (const item of [...(departmentStore.myAllDepartments ?? []), ...(departmentStore.myDepartments ?? [])]) {
        if (item?.id && item.address && !map.has(item.id)) map.set(item.id, item.address)
    }
    return map
})

const model = computed(() =>
    buildDepartmentTree(normalizeTreeRoots(departmentStore.departmentsTree), storeIds.value, addresses.value),
)

const hasTreeData = computed(() => model.value.order.length > 0)
const hasStores = computed(() => model.value.order.some((entry) => entry.isStore))
// Пустота подтверждается только успешным ответом; при ошибке показывается error/retry.
const isEmpty = computed(() =>
    !loading.value && !loadError.value && (props.mode === 'schedule' ? !hasStores.value : !hasTreeData.value),
)
const isSingleStore = computed(() =>
    !loading.value && !loadError.value && model.value.order.length === 1 && model.value.order[0].isStore,
)
const showLoader = computed(() => loading.value && !hasTreeData.value)
// Структура пригодна для выбора: успешный ответ либо ранее загруженное дерево.
const isTreeUsable = computed(() => !loading.value && (!loadError.value || hasTreeData.value))

const selectedGroup = computed(() => (props.mode === 'analytics' ? scheduleStore.analyticsGroup : null))
const selectedId = computed(() => selectedGroup.value?.id ?? scheduleStore.currentDepartment?.id)
const selectedEntry = computed(() => findEntryById(model.value, selectedId.value))
const selectedAncestors = computed(() =>
    new Set(selectedEntry.value ? ancestorKeys(model.value, selectedEntry.value.key) : []),
)
const selectedGroupCount = computed(() =>
    selectedGroup.value && selectedEntry.value?.isGroup ? selectedEntry.value.storeCount : null,
)

const triggerLabel = computed(() => {
    if (selectedGroup.value) return selectedGroup.value.name
    const current = scheduleStore.currentDepartment
    if (current?.name) return current.name
    if (isEmpty.value) return 'Нет доступных подразделений'
    return props.mode === 'analytics' ? 'Выберите подразделение' : 'Выберите магазин'
})

const normalizedQuery = computed(() => normalizeQuery(query.value))
const searchResult = computed(() =>
    normalizedQuery.value ? searchTree(model.value, normalizedQuery.value) : null,
)
const expandedSet = computed(() => (searchResult.value ? searchExpanded.value : expanded.value))
const rows = computed(() => visibleEntries(model.value, expandedSet.value, searchResult.value?.visible ?? null))

watch(searchResult, (result) => {
    if (result) {
        searchExpanded.value = new Set(result.ancestors)
        const firstMatch = rows.value.find((row) => result.matches.has(row.key))
        activeKey.value = firstMatch?.key ?? rows.value[0]?.key ?? null
    } else {
        resetExpansion()
    }
})

function indentLevel(row: DepartmentTreeEntry) {
    return Math.min(row.depth, MAX_INDENT_LEVEL)
}

function isExpanded(row: DepartmentTreeEntry) {
    return expandedSet.value.has(row.key)
}

function highlight(text: string) {
    return splitByQuery(text ?? '', normalizedQuery.value)
}

// Путь до текущего выбора раскрыт; остальные ветки свёрнуты.
function resetExpansion() {
    const entry = selectedEntry.value
    expanded.value = new Set(entry ? ancestorKeys(model.value, entry.key) : [])
    activeKey.value = entry?.key ?? model.value.rootKeys[0] ?? null
}

function setExpanded(row: DepartmentTreeEntry, value: boolean) {
    const target = searchResult.value ? searchExpanded : expanded
    const next = new Set(target.value)
    if (value) next.add(row.key)
    else next.delete(row.key)
    target.value = next
}

function toggleRow(row: DepartmentTreeEntry) {
    activeKey.value = row.key
    setExpanded(row, !isExpanded(row))
}

function openMenu() {
    if (isOpen.value || isSingleStore.value) return
    query.value = ''
    resetExpansion()
    isOpen.value = true
    nextTick(() => {
        searchRef.value?.focus()
        scrollRowIntoView(activeKey.value)
    })
}

function closeMenu(focusTrigger: boolean) {
    if (!isOpen.value) return
    isOpen.value = false
    query.value = ''
    if (focusTrigger) nextTick(() => triggerRef.value?.focus())
}

function toggleMenu() {
    if (isOpen.value) closeMenu(false)
    else openMenu()
}

function onEscape(event: KeyboardEvent) {
    if (!isOpen.value) return
    event.preventDefault()
    event.stopPropagation()
    closeMenu(true)
}

function onClickOutside() {
    if (!isOpen.value) return
    // Клик по пустому месту: фокус возвращается к полю; клик по другому контролу фокус не отнимает.
    const active = document.activeElement
    const returnFocus = !active || active === document.body || Boolean(rootRef.value?.contains(active))
    closeMenu(returnFocus)
}

// Tab за пределы меню закрывает его без смены выбора; фокус не запирается.
function onFocusOut(event: FocusEvent) {
    const next = event.relatedTarget as Node | null
    if (isOpen.value && next && !rootRef.value?.contains(next)) closeMenu(false)
}

function rowElement(key: string | null) {
    if (!key || !treeRef.value) return null
    return treeRef.value.querySelector<HTMLElement>(`[data-key="${CSS.escape(key)}"]`)
}

function scrollRowIntoView(key: string | null) {
    rowElement(key)?.scrollIntoView({ block: 'nearest' })
}

function focusRow(key: string | undefined | null) {
    if (!key) return
    activeKey.value = key
    nextTick(() => rowElement(key)?.focus())
}

function focusActiveRow() {
    const key = rows.value.some((row) => row.key === activeKey.value) ? activeKey.value : rows.value[0]?.key
    focusRow(key)
}

function findDepartment(id: string) {
    return departmentStore.myAllDepartments?.find((item) => item.id === id)
        ?? departmentStore.myDepartments?.find((item) => item.id === id)
}

// Магазин — общий контекст currentDepartment; в Расписании выбор сразу загружает график и табель.
function applyStore(id: string) {
    const department = findDepartment(id)
    if (!department) return false
    scheduleStore.setCurrentDepartment(department)
    if (props.mode === 'analytics') scheduleStore.setAnalyticsGroup(null)
    else {
        scheduleStore.fetchSchedule()
        scheduleStore.fetchTimeSheet()
    }
    return true
}

function selectStore(row: DepartmentTreeEntry) {
    if (!row.isStore) return
    if (applyStore(row.node.id)) closeMenu(true)
}

// Голова — только Аналитика; currentDepartment (магазин Расписания) не меняется.
function selectGroup(row: DepartmentTreeEntry) {
    if (!canSelectGroup.value || !row.isGroup) return
    scheduleStore.setAnalyticsGroup({ id: row.node.id, name: row.node.name })
    closeMenu(true)
}

function activateRow(row: DepartmentTreeEntry) {
    activeKey.value = row.key
    if (row.isGroup) toggleRow(row)
    else selectStore(row)
}

function onTreeKeydown(event: KeyboardEvent) {
    const list = rows.value
    const index = list.findIndex((row) => row.key === activeKey.value)
    const row = list[index]
    if (!row) return

    switch (event.key) {
        case 'ArrowDown':
            event.preventDefault()
            focusRow(list[index + 1]?.key)
            break
        case 'ArrowUp':
            event.preventDefault()
            if (index === 0) searchRef.value?.focus()
            else focusRow(list[index - 1]?.key)
            break
        case 'ArrowRight':
            event.preventDefault()
            if (!row.isGroup) break
            if (!isExpanded(row)) setExpanded(row, true)
            else if (list[index + 1]?.parentKey === row.key) focusRow(list[index + 1].key)
            break
        case 'ArrowLeft':
            event.preventDefault()
            if (row.isGroup && isExpanded(row)) setExpanded(row, false)
            else focusRow(row.parentKey)
            break
        case 'Home':
            event.preventDefault()
            focusRow(list[0]?.key)
            break
        case 'End':
            event.preventDefault()
            focusRow(list[list.length - 1]?.key)
            break
        case 'Enter':
        case ' ':
            // Кнопка «Выбрать группу» обрабатывает Enter/Space сама.
            if (event.target !== event.currentTarget && (event.target as HTMLElement).closest('button')) break
            event.preventDefault()
            activateRow(row)
            break
    }
}

// Последний ещё доступный магазин, иначе первый магазин в порядке дерева; без магазинов — пустой выбор.
function resolveSelection() {
    if (selectionResolved) return
    const treeReady = isTreeUsable.value

    const group = selectedGroup.value
    if (group) {
        if (!treeReady || findEntryById(model.value, group.id)?.isGroup) {
            selectionResolved = true
            return
        }
        scheduleStore.setAnalyticsGroup(null)
    }

    const currentId = scheduleStore.currentDepartment?.id
    if (currentId && storeIds.value?.has(currentId) && applyStore(currentId)) {
        selectionResolved = true
        return
    }
    // Неподтверждённый магазин не остаётся контекстом до ответа дерева: запросы Расписания с ним не уходят.
    if (currentId) scheduleStore.clearCurrentDepartment()
    // Порядок дерева нужен для запасного выбора: при ошибке ждём «Повторить».
    if (!treeReady) return

    const first = model.value.order.find((entry) => entry.isStore)
    if (!first || !applyStore(first.node.id)) scheduleStore.clearCurrentDepartment()
    selectionResolved = true
}

async function loadTree() {
    loadError.value = false
    if (!hasTreeData.value) loading.value = true
    const error = await departmentStore.fetchDepartmentsTree()
    loading.value = false
    loadError.value = Boolean(error)
    resolveSelection()
    // Дерево пришло при открытом меню: раскрыть путь до выбора.
    if (isOpen.value && !normalizedQuery.value) resetExpansion()
}

onMounted(loadTree)
</script>
